import { Link, useMatch } from 'react-router-dom';
import {
  BarChart3,
  CreditCard,
  Home,
  Menu,
  Mic,
  Package,
  Palette,
  Search,
  Shield,
  Trophy,
  Users,
} from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { useState } from 'react';
import NotificationBell from '@/features/notifications/NotificationBell';
import MobileNavDrawer from '@/features/artist/MobileNavDrawer';
import { useArtist } from '@/hooks/useArtist';
import { useArtistTeamAccess } from '@/hooks/useArtistTeamAccess';
import { useHomeHref } from '@/hooks/useHomeHref';
import { useNotifications } from '@/hooks/useNotifications';
import { useAuth } from '@/lib/AuthContext';
import {
  Avatar,
  LanguageSwitcher,
  LinkButton,
  Logo,
} from '@/components/ui';

/**
 * Global top bar shown on every non-auth page. Detects the current artist
 * from the URL (`/artists/:slug/*`) to light up studio shortcuts when the
 * signed-in user has team access; otherwise those buttons are hidden.
 *
 * Mobile collapses to a single hamburger that opens MobileNavDrawer.
 */
export function TopBar() {
  const { t } = useTranslation();
  const { session, profile, isPremium, isAdmin, signOut } = useAuth();
  const signedIn = Boolean(session);

  // Sniff the current artist slug from the URL so studio buttons work on
  // any nested artist route (feed, community, store, studio/*, …).
  const artistMatch = useMatch('/artists/:slug/*');
  const artistSlug = artistMatch?.params.slug ?? null;
  const { artist } = useArtist(artistSlug ?? undefined);

  const team = useArtistTeamAccess(artist?.id);
  const { unreadCount } = useNotifications();
  const homeHref = useHomeHref();
  // The logo/wordmark always goes to Dubs Home, the single front door.
  const brandHref = '/';
  const [mobileOpen, setMobileOpen] = useState(false);

  const displayName = profile?.display_name ?? 'you';
  const handle = profile?.handle ?? null;
  const avatarSeed = profile?.id ?? session?.user.id ?? 'me';
  const avatarUrl = profile?.avatar_url ?? null;
  const onSignOut = () => void signOut();

  return (
    <header className="border-b border-[color:var(--color-border)] bg-surface pt-[env(safe-area-inset-top)]">
      <div className="mx-auto flex h-16 w-full max-w-6xl items-center justify-between px-4 lg:px-10">
        <div className="flex items-center gap-2">
          <Link
            to={brandHref}
            aria-label="Dubs home"
            className="inline-flex items-center rounded-md transition hover:opacity-80"
          >
            <Logo size={24} />
          </Link>
        </div>

        {/* Mobile: single hamburger. Everything else moves into the drawer. */}
        <button
          type="button"
          onClick={() => setMobileOpen(true)}
          aria-label="Open menu"
          className="relative inline-flex size-10 items-center justify-center rounded-[10px] border border-[color:var(--color-border)] bg-surface-2 text-on-surface transition hover:text-primary md:hidden"
        >
          <Menu className="size-5" aria-hidden />
          {signedIn && unreadCount > 0 && (
            <span
              aria-hidden
              className="absolute right-1.5 top-1.5 size-2 rounded-full border border-black/40 bg-primary"
            />
          )}
        </button>

        {/* Desktop: keep the full icon cluster. */}
        <div className="hidden items-center gap-2 md:flex">
          <Link
            to={homeHref}
            aria-label="Home"
            className="inline-flex size-9 items-center justify-center rounded-[10px] border border-[color:var(--color-border)] bg-surface-2 text-on-surface transition hover:text-primary"
          >
            <Home className="size-[18px]" aria-hidden />
          </Link>
          <Link
            to="/search"
            aria-label={t('topBar.search')}
            className="inline-flex size-9 items-center justify-center rounded-[10px] border border-[color:var(--color-border)] bg-surface-2 text-on-surface transition hover:text-primary"
          >
            <Search className="size-[18px]" aria-hidden />
          </Link>
          {signedIn && <NotificationBell />}
          <LanguageSwitcher />
          {signedIn && artist && team.canManage && (
            <>
              <Link
                to={`/artists/${artist.slug}/studio`}
                aria-label="Artist Studio"
                className="inline-flex size-9 items-center justify-center rounded-[10px] border border-primary bg-[color:var(--color-primary-dim)] text-primary transition hover:bg-primary hover:text-[color:var(--color-primary-ink)]"
              >
                <Mic className="size-[18px]" aria-hidden />
              </Link>
              <Link
                to={`/artists/${artist.slug}/studio/settings`}
                aria-label="Artist branding"
                className="inline-flex size-9 items-center justify-center rounded-[10px] border border-[color:var(--color-border)] bg-surface-2 text-on-surface transition hover:text-primary"
              >
                <Palette className="size-[18px]" aria-hidden />
              </Link>
              <Link
                to={`/artists/${artist.slug}/studio/analytics`}
                aria-label="Artist analytics"
                className="inline-flex size-9 items-center justify-center rounded-[10px] border border-[color:var(--color-border)] bg-surface-2 text-on-surface transition hover:text-primary"
              >
                <BarChart3 className="size-[18px]" aria-hidden />
              </Link>
              {team.canOwn && (
                <Link
                  to={`/artists/${artist.slug}/studio/team`}
                  aria-label="Team management"
                  className="inline-flex size-9 items-center justify-center rounded-[10px] border border-[color:var(--color-border)] bg-surface-2 text-on-surface transition hover:text-primary"
                >
                  <Users className="size-[18px]" aria-hidden />
                </Link>
              )}
            </>
          )}
          {signedIn && (
            <>
              <Link
                to="/rewards"
                aria-label="Rewards"
                className="inline-flex size-9 items-center justify-center rounded-[10px] border border-[color:var(--color-border)] bg-surface-2 text-on-surface transition hover:text-primary"
              >
                <Trophy className="size-[18px]" aria-hidden />
              </Link>
              <Link
                to="/orders"
                aria-label={t('topBar.orders')}
                className="inline-flex size-9 items-center justify-center rounded-[10px] border border-[color:var(--color-border)] bg-surface-2 text-on-surface transition hover:text-primary"
              >
                <Package className="size-[18px]" aria-hidden />
              </Link>
              <Link
                to="/membership"
                aria-label="Membership"
                className="inline-flex size-9 items-center justify-center rounded-[10px] border border-[color:var(--color-border)] bg-surface-2 text-on-surface transition hover:text-primary"
              >
                <CreditCard className="size-[18px]" aria-hidden />
              </Link>
            </>
          )}
          {signedIn && isAdmin && (
            <Link
              to="/admin/moderation"
              aria-label={t('topBar.admin')}
              className="inline-flex size-9 items-center justify-center rounded-[10px] border border-[color:var(--color-border)] bg-surface-2 text-on-surface transition hover:text-primary"
            >
              <Shield className="size-[18px]" aria-hidden />
            </Link>
          )}

          {!isPremium && (
            <LinkButton
              to="/membership"
              variant="accent"
              size="sm"
              className="ml-1 hidden sm:inline-flex"
            >
              {t('topBar.goSuperfan')}
            </LinkButton>
          )}

          {signedIn ? (
            <div className="ml-2 flex items-center gap-2 rounded-full border border-[color:var(--color-border)] bg-surface-2 pl-1 pr-3 py-1">
              <Link
                to={handle ? `/@${handle}` : '/settings/profile'}
                aria-label="Your profile"
                className="flex items-center gap-2"
              >
                <Avatar
                  seed={avatarSeed}
                  name={displayName}
                  src={avatarUrl}
                  size={28}
                />
                <span className="hidden text-sm font-medium text-on-surface-dim hover:text-on-surface sm:inline">
                  {displayName}
                </span>
              </Link>
              <button
                type="button"
                onClick={onSignOut}
                className="text-xs font-semibold text-on-surface-muted transition hover:text-primary"
              >
                {t('topBar.signOut')}
              </button>
            </div>
          ) : (
            <LinkButton to="/login" variant="ghost" size="sm" className="ml-1">
              {t('topBar.signIn')}
            </LinkButton>
          )}
        </div>
      </div>

      <MobileNavDrawer
        open={mobileOpen}
        onClose={() => setMobileOpen(false)}
        signedIn={signedIn}
        isPremium={isPremium}
        isAdmin={isAdmin}
        canManage={Boolean(artist && team.canManage)}
        canOwn={Boolean(artist && team.canOwn)}
        artistSlug={artist?.slug ?? ''}
        displayName={displayName}
        handle={handle}
        avatarSeed={avatarSeed}
        avatarUrl={avatarUrl}
        unreadCount={unreadCount}
        onSignOut={onSignOut}
      />
    </header>
  );
}

export default TopBar;
